import {
  JSONPatchDocument,
  ModelWithMeta,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import {
  all,
  call,
  getContext,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { MODULE_NAME } from "../moduleName";
import { expansionApplicationEvidenceService, orgScopesService, scopeExpansionService } from "../services";
import {
  ActionPayload,
  ASSIGNING_ITEM,
  CLOSING_APPLICATION,
  CREATED_ITEM,
  CREATING_ITEM,
  CREATING_ITEM_VALIDATION_FAIL,
  loadedAdditionalEvidence,
  loadedEvidenceRequest,
  loadedItem,
  loadedOrganisationScopes,
  loadingItem,
  LOADING_EVIDENCE_REQUEST,
  LOADING_ITEM,
  LOADING_ORGANISATION_SCOPES,
  SENDING_DATE,
  SENDING_DOCUMENTS,
  SENDING_EVIDENCE,
  SENDING_MESSAGE,
  SENDING_NOTE,
  SUBMITTED_ITEM,
  SUBMITTING_EVIDENCE,
  SUBMITTING_ITEM,
  SUBMITTING_ITEM_VALIDATION_FAIL,
  SUBMITTING_UPDATE_ITEM,
  SUBMITTING_UPDATE_ITEM_VALIDATION_FAIL,
  UPDATING_EVIDENCE_REQUEST,
  UPDATING_ITEM,
  UPDATING_ITEM_VALIDATION_FAIL,
  UPDATING_OTHER_EVIDENCE_REQUEST,
  UPDATING_SCOPES,
  WITHDRAWING_APPLICATION,
} from "./actions";
import { authSelector } from "./selectors";
import { History } from "history";
import { ApplicationResponse, ScopeApplication } from "../domain/ScopeApplication";
import { Scope } from "../domain/Scope";
import { Attachment } from "../domain/Attachment";
import { refreshActivityFeed } from "@ofqual/portal-activity-feed";
import { DateEvent } from "../domain/DateEvent";
import { AdditionalEvidenceRequest } from "../domain/AdditionalEvidenceRequest";
import { UserType } from "./types";
import { ActivityType } from "../domain/ActivityType";

function getRefreshAction(id: string, userType: UserType) {
  let params: { activityTypes?: ActivityType[] } = {};
  if (userType === UserType.AO) {
    params = { 
      activityTypes: [
        ActivityType.ScopeMessageAo,
        ActivityType.ScopeMessageOfqual,
        ActivityType.ScopeAdditionalEvidenceRequest,
        ActivityType.ScopeClosed,
        ActivityType.ScopeChanged,
        ActivityType.ScopeAdditionalEvidenceSubmitted,
      ]
    };
  }
  
  return refreshActivityFeed(id, params);
}

function* navigate(url: string) {
  const history: History = yield getContext("history");
  yield call(() => history.push(url));
}


function* watchItemSaga() {
  yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchSubmitUpdateItemSaga() {
  yield takeEvery(SUBMITTING_UPDATE_ITEM, submitUpdateItemSaga);
}

function* watchUpdateItemSaga() {
  yield takeEvery(UPDATING_ITEM, updateItemSaga);
}

function* watchCreateItemSaga() {
  yield takeEvery(CREATING_ITEM, createItemSaga);
}

function* watchSubmitItemSaga() {
  yield takeEvery(SUBMITTING_ITEM, submitItemSaga);
}

function* watchAssignItemSaga() {
  yield takeEvery(ASSIGNING_ITEM, assignItemSaga);
}

function* watchUpdateScopeSaga() {
  yield takeEvery(UPDATING_SCOPES, updateScopesSaga);
}

function* watchSendNoteSaga() {
  yield takeEvery(SENDING_NOTE, sendNoteSaga);
}

function* watchSendDocumentSaga() {
  yield takeEvery(SENDING_DOCUMENTS, sendDocumentsSaga);
}
function* watchSendDateSaga() {
  yield takeEvery(SENDING_DATE, sendDateSaga);
}

function* watchCloseApplicationSaga() {
  yield takeEvery(CLOSING_APPLICATION, closeApplicationSaga);
}

function* watchAdditionalEvidenceSaga() {
  yield takeEvery(SENDING_EVIDENCE, additionalEvidenceSaga);
}

function* watchSendMessageSaga() {
  
  yield takeEvery(SENDING_MESSAGE, sendMessageSaga);
}
function* watchWithdrawApplicationSaga() {
  yield takeEvery(WITHDRAWING_APPLICATION, withdrawApplicationSaga);
}

function* watchLoadEvidenceSaga() {
  yield takeLatest(LOADING_EVIDENCE_REQUEST, loadEvidenceRequestSaga);
}

function* watchUpdateEvidenceSaga() {
  yield takeEvery(UPDATING_EVIDENCE_REQUEST, updateEvidenceSaga);
}

function* watchUpdateOtherEvidenceSaga() {
  yield takeEvery(UPDATING_OTHER_EVIDENCE_REQUEST, updateOtherEvidenceSaga);
}

function* watchSubmitEvidenceSaga() {
  yield takeEvery(SUBMITTING_EVIDENCE, submitEvidenceSaga);
}

function* watchGetOrganisationScopesSaga() {
  yield takeLatest(LOADING_ORGANISATION_SCOPES, getOrganisationScopesSaga);
}

function* itemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { orgId, id } = payload as {
    orgId: string;
    id: string;
  };

  const { application, evidenceRequests } = yield all({
    application: call(
      [scopeExpansionService, scopeExpansionService.get],
      orgId,
      id,
      {
        token,
      }
    ),
    evidenceRequests: call([scopeExpansionService, scopeExpansionService.getAdditionalEvidenceRequests],
      orgId,
      id,
      {
        token,
      }
    )
  });
  yield put(loadedItem(application.item, application.scopes, application.attachments, evidenceRequests));
}

function* updateItemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { orgId, id, patchDocument, scopes, documentsToDelete } = payload as {
    orgId: string,
    id: string;
    patchDocument: JSONPatchDocument;
    scopes: Scope[];
    documentsToDelete: Attachment[];
  };

  const validatedResponse: ValidatedResponse<ScopeApplication> = yield call(
    [scopeExpansionService, scopeExpansionService.update],
    orgId,
    id,
    patchDocument,
    scopes,
    documentsToDelete,
    {
      token,
    }
  );

  if (validatedResponse.isValid) {
    yield put(loadingItem(orgId, id));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* submitUpdateItemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
    const { orgId, id, patchDocument, scopes, documentsToDelete } = payload as {
    orgId: string;
    id: string;
    patchDocument: JSONPatchDocument;
    scopes: Scope[];
    documentsToDelete: Attachment[];
  };
  const validatedResponse: ValidatedResponse<ScopeApplication> = yield call(
    [scopeExpansionService, scopeExpansionService.submitUpdate],
    orgId,
    id,
    patchDocument,
    scopes,
    documentsToDelete,
    {
      token,
    }
  );
  if (validatedResponse.isValid) {
    yield put(loadingItem(orgId, id));
  } else {
    yield put({
      type: SUBMITTING_UPDATE_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* createItemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
    const { orgId, item, scopes } = payload as {
    orgId: string;
    item: ScopeApplication;
    scopes: Scope[];
  };
  const validatedResponse: ValidatedResponse<ScopeApplication> = yield call(
    [scopeExpansionService, scopeExpansionService.create],
    orgId,
    item,
    scopes,
    {
      token,
    }
  );
  if (validatedResponse.isValid) {
    yield put({ type: CREATED_ITEM });
    yield call(() => navigate(`/organisations/${orgId}/${MODULE_NAME}/applications/${validatedResponse.data.id}`));
  } else {
    yield put({
      type: CREATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* submitItemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
    const { orgId, item, scopes } = payload as {
    orgId: string;
    item: ScopeApplication;
    scopes: Scope[];
  };
  const validatedResponse: ValidatedResponse<ScopeApplication> = yield call(
    [scopeExpansionService, scopeExpansionService.submit],
    orgId,
    item,
    scopes,
    {
      token,
    }
  );
  if (validatedResponse.isValid) {
    yield put({ type: SUBMITTED_ITEM });
    yield call(() => navigate(`/organisations/${orgId}/${MODULE_NAME}/applications/${validatedResponse.data.id}`));
  } else {
    yield put({
      type: SUBMITTING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* assignItemSaga({
  payload,
}: ActionPayload<{
  orgId: string;
  id: string;
  assignedToUpn: string;
  assignedToDisplayName: string;
  message: string;
}>) {
  const { token, userType } = yield select(authSelector);
  const { orgId, id, assignedToUpn, assignedToDisplayName, message } = payload;

  const response: ApplicationResponse = yield call(
    [scopeExpansionService, scopeExpansionService.assign],
    orgId,
    id,
    assignedToUpn,
    assignedToDisplayName,
    message,
    {
      token,
    }
  );
  yield put(getRefreshAction(id, userType));
  yield put(loadedItem(response.item, response.scopes, response.attachments));
}

function* updateScopesSaga({
  payload,
}: ActionPayload<{
  orgId: string;
  id: string;
  modelsWithMeta: ModelWithMeta<Scope>[];
  message: string;
}>) {
  const { token, userType } = yield select(authSelector);
  const { orgId, id, modelsWithMeta, message } = payload;

  const response: ApplicationResponse = yield call(
    [scopeExpansionService, scopeExpansionService.updateScopes],
    orgId,
    id,
    modelsWithMeta,
    message,
    {
      token,
    }
  );
  yield put(getRefreshAction(id, userType));
  yield put(loadedItem(response.item, response.scopes, response.attachments));
}

function* sendNoteSaga({
  payload,
}: ActionPayload<{ orgId: string; id: string; note: string }>) {
  const { token, userType } = yield select(authSelector);
  const { orgId, id, note } = payload;

  yield call(
    [scopeExpansionService, scopeExpansionService.sendNote],
    orgId,
    id,
    note,
    {
      token,
    }
  );
  yield put(getRefreshAction(id, userType));
}

function* sendDocumentsSaga({
  payload,
}: ActionPayload<{ orgId: string; id: string; files: File[]; documentType: string; note: string }>) {
  const { token, userType } = yield select(authSelector);
  const { orgId, id, files, documentType, note } = payload;

  yield call(
    [scopeExpansionService, scopeExpansionService.sendDocuments],
    orgId,
    id,
    files,
    documentType,
    note,
    {
      token,
    }
  );
  yield put(getRefreshAction(id, userType));
}

function* sendDateSaga({
  payload,
}: ActionPayload<{ orgId: string; id: string; dateEvent: DateEvent }>) {
  const { token, userType } = yield select(authSelector);
  const { orgId, id, dateEvent } = payload;

  yield call(
    [scopeExpansionService, scopeExpansionService.sendDate],
    orgId,
    id,
    dateEvent,
    {
      token,
    }
  );
  yield put(getRefreshAction(id, userType));
}

function* closeApplicationSaga({
  payload,
}: ActionPayload<{ orgId: string; id: string; letter: File; note: string }>) {
  const { token, userType } = yield select(authSelector);
  const { orgId, id, letter, note } = payload;

  const response: ApplicationResponse = yield call(
    [scopeExpansionService, scopeExpansionService.closeApplication],
    orgId,
    id,
    note,
    letter,
    {
      token,
    }
  );
  yield put(getRefreshAction(id, userType));
  yield put(loadedItem(response.item, response.scopes, response.attachments));
}

function* additionalEvidenceSaga({
  payload,
}: ActionPayload<{ orgId: string; id: string; evidenceEvent: AdditionalEvidenceRequest }>) {
  const { token, userType } = yield select(authSelector);
  const { orgId, id, evidenceEvent } = payload;

  const response: AdditionalEvidenceRequest[] = yield call(
    [scopeExpansionService, scopeExpansionService.sendAdditionalEvidence],
    orgId,
    id,
    evidenceEvent,
    {
      token,
    }
  );
  yield put(getRefreshAction(id, userType));
  yield put(loadedAdditionalEvidence(response))
}

function* sendMessageSaga({
  payload,
}: ActionPayload<{ orgId: string; id: string; message: string }>) {
  const { token, userType } = yield select(authSelector);
  const { orgId, id, message } = payload;

  yield call(
    [scopeExpansionService, scopeExpansionService.sendMessage],
    orgId,
    id,
    message,
    {
      token,
    }
  );

  yield put(getRefreshAction(id, userType));
}

function* withdrawApplicationSaga({
  payload,
}: ActionPayload<{ orgId: string; id: string; note: string }>) {
  const { token, userType } = yield select(authSelector);
  const { orgId, id, note } = payload;

  const response: ApplicationResponse = yield call(
    [scopeExpansionService, scopeExpansionService.withdrawApplication],
    orgId,
    id,
    note,
    {
      token,
    }
  );

  yield put(getRefreshAction(id, userType));
  yield put(loadedItem(response.item, response.scopes, response.attachments));
}

function* loadEvidenceRequestSaga({
  payload,
}: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { orgId, id, requestId } = payload as {
    orgId: string;
    id: string;
    requestId: string;
  };
  const item: AdditionalEvidenceRequest = yield call(
    [expansionApplicationEvidenceService, expansionApplicationEvidenceService.get],
    orgId,
    id,
    requestId,
    {
      token,
    }
  );
  yield put(loadedEvidenceRequest(item));
}

function* updateEvidenceSaga({
  payload,
}: ActionPayload<{
  orgId: string;
  id: string;
  requestId: string;
  evidence: AdditionalEvidenceRequest;
}>) {
  const { token } = yield select(authSelector);
  const { orgId, id, requestId, evidence } = payload;

  const response: AdditionalEvidenceRequest = yield call(
    [expansionApplicationEvidenceService, expansionApplicationEvidenceService.update],
    orgId,
    id,
    requestId,
    evidence,
    {
      token,
    }
  );
  yield put(loadedEvidenceRequest(response));
}

function* updateOtherEvidenceSaga({
  payload,
}: ActionPayload<{
  orgId: string;
  id: string;
  requestId: string;
  files: File[];
  evidence: AdditionalEvidenceRequest;
}>) {
  const { token } = yield select(authSelector);
  const { orgId, id, requestId, files, evidence } = payload;

  const response: AdditionalEvidenceRequest = yield call(
    [expansionApplicationEvidenceService, expansionApplicationEvidenceService.updateOtherEvidence],
    orgId,
    id,
    requestId,
    files,
    evidence,
    {
      token,
    }
  );
  yield put(loadedEvidenceRequest(response));
}

function* submitEvidenceSaga({
  payload,
}: ActionPayload<{
  orgId: string;
  id: string;
  requestId: string;
  timestamp: string;
}>) {
  const { token, userType } = yield select(authSelector);
  const { orgId, id, requestId, timestamp } = payload;

  yield call(
    [expansionApplicationEvidenceService, expansionApplicationEvidenceService.submit],
    orgId,
    id,
    requestId,
    timestamp,
    {
      token,
    }
  );
  yield put(getRefreshAction(id, userType));
}


export function* getOrganisationScopesSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);

  const response: Scope[] = yield call(
    [orgScopesService, orgScopesService.get],
    payload,
    {
      token: token,
    }
  );

  yield put(loadedOrganisationScopes(response));
}


export function* rootSaga() {
  yield all([
    watchItemSaga(),
    watchUpdateItemSaga(),
    watchCreateItemSaga(),
    watchSubmitItemSaga(),
    watchSubmitUpdateItemSaga(),
    watchAssignItemSaga(),
    watchUpdateScopeSaga(),
    watchSendNoteSaga(),
    watchSendDocumentSaga(),
    watchSendDateSaga(),
    watchCloseApplicationSaga(),
    watchAdditionalEvidenceSaga(),
    watchSendMessageSaga(),
    watchWithdrawApplicationSaga(),
    watchLoadEvidenceSaga(),
    watchUpdateEvidenceSaga(),
    watchUpdateOtherEvidenceSaga(),
    watchSubmitEvidenceSaga(),
    watchGetOrganisationScopesSaga()
  ]);
}
